/**
 * Central Afterpay Pay Page Launcher
 * - Auto-launch when ?autostart=1 and (token present OR we can request one)
 * - Friendly error + retry button (no auto-launch when returning with ?error=...)
 * - Detects popup blocked; falls back to redirect flow
 * - Analytics: afterpay_start, afterpay_error
 */

(() => {
  'use strict';

  const DEBUG = true;

  // ===== Utilities =====
  const qs = (sel) => document.querySelector(sel);
  const $err = qs('#ap-error');
  const $retry = qs('#ap-retry');
  const $retrySpin = qs('#ap-retry-spin');
  const $subtitle = qs('#ap-subtitle');

  const params = new URLSearchParams(location.search);
  const urlToken = params.get('token') || '';
  const autoStart = params.get('autostart') === '1';
  const errorCode = params.get('error') || '';
  const errorMsg = params.get('message') || '';

  const CONFIRM_PAGE = '/pages/afterpay-confirmation';

  const AFTERPAY_GATEWAY_API_URL = 'https://lvi88fhcpg.execute-api.ap-southeast-2.amazonaws.com/prod/afterpay-gateway';

  const show = (el) => el && el.classList.remove('hidden');
  const hide = (el) => el && el.classList.add('hidden');
  const setText = (el, text) => { if (el) el.textContent = text; };

  const $errorModal = qs('#ap-error-modal');

  const showErrorModal = () => {
    if ($errorModal) $errorModal.classList.remove('hidden');
  };

  const setBusy = (b) => {
    if (!$retry) return;
    $retry.toggleAttribute('disabled', !!b);
    if (b) {
      show($retrySpin);
      $retry.setAttribute('aria-busy', 'true');
    } else {
      hide($retrySpin);
      $retry.removeAttribute('aria-busy');
    }
  };

  const friendlyError = (code, msg) => {
    if (!$err) return;
    const map = {
      'popup_blocked': 'Your browser blocked the Afterpay window.',
      'cancelled': 'Payment was cancelled.',
      'token_invalid': 'Your payment session expired. Please try again.',
      'network': 'Network error. Please check your connection and retry.',
      'unknown': 'Something went wrong. Please try again.'
    };
    const safe = map[code] || map['unknown'];
    setText($err, msg ? `${safe} (${msg})` : safe);
    show($err);
  };

  // ===== Token helpers =====
  async function fetchCart() {
    const res = await fetch('/cart.js', { credentials: 'same-origin' });
    if (!res.ok) throw new Error('cart_fetch_failed');
    return res.json();
  }

  function buildItems(cart) {
    return (cart.items || []).map(item => ({
      name: item.product_title,
      sku: item.sku || '',
      quantity: item.quantity,
      price: {
        amount: (item.price / 100).toFixed(2),
        currency: 'AUD',
      }
    }));
  }

  async function getTokenIfNeeded() {
    if (urlToken) return urlToken;

    // Build payload from current cart
    const cart = await fetchCart();
    const amount = (cart.total_price / 100).toFixed(2);

    const res = await fetch(`${AFTERPAY_GATEWAY_API_URL}/afterpay/checkout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        amount,
        currency: 'AUD',
        items: buildItems(cart),
        popupOriginUrl: location.href,
        mode: 'express'
      })
    });

    if (!res.ok) throw new Error('token_create_failed');
    const data = await res.json();
    if (!data?.token) throw new Error('token_missing');
    return data.token;
  }

  // ===== Afterpay Launchers =====
  function ensureSdkReady() {
    return new Promise((resolve, reject) => {
      let tries = 0;
      const maxTries = 40; // ~4s
      const t = setInterval(() => {
        tries++;
        if (window.AfterPay && typeof window.AfterPay.initialize === 'function') {
          clearInterval(t);
          resolve();
        } else if (tries >= maxTries) {
          clearInterval(t);
          reject(new Error('sdk_not_ready'));
        }
      }, 100);
    });
  }

  function getBundleState() {
    const bundleStateRaw = localStorage.getItem('personalised_bundle_state');
    let bundleState;

    try {
      bundleState = JSON.parse(bundleStateRaw);
      return bundleState;
    } catch (e) {
      console.warn('Invalid personalised_bundle_state');
      return null;
    }
  }

  async function loadAfterpaySdk() {
    if (window.AfterPay) {
      return; // Already loaded
    }

    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://portal.afterpay.com/afterpay.js';
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  async function removeRechargeBundleFromCart(cart, rc_bundle_id) {
    const updates = {};

    cart.items.forEach(item => {
      if (item.properties && item.properties._rc_bundle === rc_bundle_id) {
        updates[item.key] = 0;
      }
    });

    if (!Object.keys(updates).length) return false;

    const res = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ updates })
    });

    if (!res.ok) throw new Error('cart/update.js failed');

    return res.json();
  }

  async function startFlow() {
    setBusy(true);
    hide($err);
    setText($subtitle, 'Launching Afterpay…');

    try {
      const token = await getTokenIfNeeded();

      const res = await fetch('/cart.js', { credentials: 'same-origin' });
      if (!res.ok) throw new Error('Cart fetch failed');
      const cartData = await res.json();
      const bundleState = getBundleState();

      window.AfterPay.initialize({
        countryCode: 'AU',
        shippingOptionRequired: false,
        addressMode: window.AfterPay.ADDRESS_MODES.SHIP_TO_ORDER_ADDRESS_WITHOUT_SHIPPING_OPTIONS
      });

      window.AfterPay.initializeForPopup({
        countryCode: 'AU',
        shippingOptionRequired: false,
        addressMode: window.AfterPay.ADDRESS_MODES.SHIP_TO_ORDER_ADDRESS_WITHOUT_SHIPPING_OPTIONS,
        target: '#ap-popup-anchor'
      });

      window.AfterPay.onComplete = async function () {
        if (DEBUG) console.debug('[AP] onComplete fired');
        try {
          const firstItem = cartData.items[0];
          const rc_bundle_id = firstItem?.properties?._rc_bundle;
          const external_product_id = firstItem.properties._rc_bundle?.split(':')[1] || firstItem.product_id;
          const external_variant_id = firstItem.properties._rc_bundle_variant?.toString() || firstItem.variant_id;
          const contents = cartData.items.map(item => ({
            collection_id: '512297271595',
            collection_source: 'shopify',
            external_product_id: item.product_id.toString(),
            external_variant_id: item.id.toString(),
            quantity: item.quantity
          }));
          const total = (cartData.total_price / 100).toFixed(2);

          const body = {
            token,
            amount: total,
            product_title: 'Personalised Pack',
            external_product_id,
            external_variant_id,
            order_interval_frequency: bundleState.frequency,
            charge_interval_frequency: bundleState.frequency,
            order_interval_unit: 'day',
            quantity: 1,
            contents
          };

          if (cartData.cart_level_discount_applications && cartData.cart_level_discount_applications.length > 0) {
            body.discount_code = cartData.cart_level_discount_applications[0].title;
          }

          const confirmRes = await fetch(`${AFTERPAY_GATEWAY_API_URL}/afterpay/confirm`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(body)
          });

          if (!confirmRes.ok) {
            const errorData = await confirmRes.json();
            throw new Error(errorData.message || 'Afterpay confirmation failed');
          }

          const result = await confirmRes.json();
          if (DEBUG) console.log('Subscription created:', result.subscription);

          const coupon =
            (cartData.cart_level_discount_applications &&
              cartData.cart_level_discount_applications[0] &&
              cartData.cart_level_discount_applications[0].title) || '';

          const ORDER_ID = String(result?.subscription?.id || token);
          const ORDER_VALUE = Number(total);
          const ORDER_TAX = 0;
          const ORDER_SHIPPING = 0;
          const ORDER_COUPON = coupon;

          sessionStorage.setItem('afterpay:purchase', JSON.stringify({
            ORDER_ID,
            ORDER_VALUE,
            ORDER_TAX,
            ORDER_SHIPPING,
            ORDER_COUPON,
            ORDER_CURRENCY: 'AUD'
          }));
          sessionStorage.setItem('ap_checkout_completed', "true");

          if (rc_bundle_id) {
            await removeRechargeBundleFromCart(cartData, rc_bundle_id);
          }

          window.location.href = CONFIRM_PAGE;
        } catch (e) {
          if (DEBUG) console.error('Afterpay confirmation error:', e.message);
          friendlyError('unknown', '');
          hide($subtitle);
          show($retry);
          showErrorModal();
        }
      };

      // Try opening the popup in the user gesture
      try {
        window.AfterPay.open();
      } catch (e) {
        console.log('open error', e);
        if (DEBUG) console.warn('[AP] open() blocked:', e);
      }

      // …then transfer token; if transfer fails, consider popup failed
      try {
        window.AfterPay.transfer({
          token,
          addressMode: window.AfterPay.ADDRESS_MODES.SHIP_TO_ORDER_ADDRESS_WITHOUT_SHIPPING_OPTIONS
        });
      } catch (e) {
        if (DEBUG) console.error('[AP] transfer() failed:', e);
        opened = false;
      }
    } catch (e) {
      if (DEBUG) console.error('startFlow error:', e);
      friendlyError('unknown', e?.message);
      show($retry);
      setText($subtitle, 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  }

  // ===== Boot =====
  function bindRetry() {
    if (!$retry) return;
    $retry.addEventListener('click', (ev) => {
      ev.preventDefault();
      startFlow();
    });
  }

  function handleErrorFromUrl() {
    if (!errorCode) return false;
    // If URL contains ?error=..., we DO NOT auto-launch
    friendlyError(errorCode, errorMsg);
    setText($subtitle, 'We couldn’t start Afterpay.');
    show($retry);
    return true;
  }

  document.addEventListener('DOMContentLoaded', async () => {
    bindRetry();

    try {
      await loadAfterpaySdk();
      await ensureSdkReady();
      // sdkReady = true;
      // // có thể bật nút nếu bạn đang disable sẵn
      $retry?.removeAttribute('disabled');
    } catch (e) {
      console.warn('[AP] SDK not ready yet:', e);
    }

    // If an error came back from backend → show error, no auto start
    const stoppedByError = handleErrorFromUrl();
    if (stoppedByError) return;

    // Only auto-launch when explicitly asked to
    if (autoStart) {
      await startFlow();
    } else {
      // No autostart → just show the button
      show($retry);
      setText($subtitle, 'Click the button to continue with Afterpay.');
    }
  });
})();
